import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogIn, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { authLogin, formatApiError } from "../lib/api";
import { useUser } from "../context/UserContext";
import usePageMeta from "../lib/usePageMeta";
import { toast } from "sonner";

export default function Login() {
  usePageMeta({
    title: "Log In — CheatCraft",
    description: "Log in to your CheatCraft account to place orders, track deliveries and manage your wallet.",
    canonicalPath: "/login",
  });
  const nav = useNavigate();
  const loc = useLocation();
  const { setUser } = useUser();
  const [form, setForm] = useState({ email: "", password: "", remember: true });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await authLogin(form);
      setUser(u);
      toast.success(`Welcome back, ${u.display_name}`);
      const redirect = loc.state?.from || "/dashboard";
      nav(redirect, { replace: true });
    } catch (err) {
      setError(formatApiError(err, "Login failed."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[80vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="text-[#00ff88] text-3xl leading-none" style={{ textShadow: "0 0 18px rgba(0,255,136,0.5)" }}>◈</span>
            <span className="text-2xl font-semibold tracking-tight text-white">CHEATCRAFT</span>
          </Link>
        </div>

        <div className="cc-surface rounded-2xl p-7 border border-[#15152a]">
          <div className="cc-overline inline-flex items-center gap-2 mb-1">
            <LogIn size={11} /> Sign in
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-white mb-1">Welcome back</h1>
          <p className="text-sm text-[#a0a0b0] mb-6">Log in to your account to continue.</p>

          <form onSubmit={submit} className="space-y-4">
            <Field label="Email">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#606070]"><Mail size={14} /></span>
                <input
                  data-testid="login-email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@studio.gg"
                  className="w-full bg-[#0d0d1a] border border-[#15152a] rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder-[#606070] cc-focus-ring"
                  required
                />
              </div>
            </Field>

            <Field label="Password">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#606070]"><Lock size={14} /></span>
                <input
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full bg-[#0d0d1a] border border-[#15152a] rounded-lg pl-9 pr-10 py-2.5 text-sm text-white placeholder-[#606070] cc-focus-ring"
                  required
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#606070] hover:text-white"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </Field>

            <div className="flex items-center justify-between">
              <label data-testid="login-remember" className="flex items-center gap-2 cursor-pointer text-xs text-[#a0a0b0]">
                <input
                  type="checkbox"
                  checked={form.remember}
                  onChange={(e) => setForm({ ...form, remember: e.target.checked })}
                  className="accent-[#00ff88]"
                />
                Remember me
              </label>
              <Link
                to="/forgot-password"
                data-testid="login-forgot-link"
                className="text-xs text-[#00ff88] hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            {error && (
              <div data-testid="login-error" className="text-xs text-[#ff8888] bg-[#ff4444]/5 border border-[#ff4444]/30 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              disabled={loading}
              className="cc-btn-primary w-full rounded-lg px-6 py-2.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <><Spinner /> Signing in…</>
              ) : (
                <><LogIn size={14} /> Log in</>
              )}
            </button>
          </form>

          <div className="text-center text-xs text-[#a0a0b0] mt-5">
            Don&apos;t have an account?{" "}
            <Link to="/signup" data-testid="login-signup-link" className="text-[#00ff88] hover:underline">
              Sign up
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}

const Spinner = () => (
  <span className="w-3.5 h-3.5 border-2 border-[#06060c]/30 border-t-[#06060c] rounded-full animate-spin" />
);

const Field = ({ label, children }) => (
  <div>
    <div className="cc-overline mb-1.5">{label}</div>
    {children}
  </div>
);
